import {useEffect, useMemo, useRef, useState} from "react";
import {Button, Form, InputGroup} from "react-bootstrap";

export function getPropertyId(property) {
    return String(property?.property_id ?? property?.id ?? "");
}

export function getPropertyLabel(property) {
    const propertyAddress = property?.full_address?.trim();
    return propertyAddress || getPropertyId(property);
}

export function normalizePropertySearchText(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s*\/\s*/g, "/")
        .replace(/\s+/g, " ");
}

export default function PropertySearchSelect({
    options,
    required,
    searchText,
    selectedProperty,
    onSearchTextChange,
    onSelectProperty,
}) {
    const containerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const filteredOptions = useMemo(() => {
        const searchTerm = normalizePropertySearchText(searchText);

        if (!searchTerm) {
            return options;
        }

        return options.filter((property) => {
            const searchableText = normalizePropertySearchText([
                getPropertyLabel(property),
                getPropertyId(property),
                property?.cos_customer_name,
                property?.agent_name,
            ]
                .filter(Boolean)
                .join(" "));

            return searchableText.includes(searchTerm);
        });
    }, [options, searchText]);

    useEffect(() => {
        setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
    }, [filteredOptions]);

    useEffect(() => {
        function handlePointerDown(event) {
            if (!containerRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handlePointerDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
        };
    }, []);

    function handleInputChange(event) {
        onSearchTextChange(event.target.value);
        setIsOpen(true);
    }

    function handleSelect(property) {
        onSelectProperty(property);
        setIsOpen(false);
    }

    function handleKeyDown(event) {
        if (!isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            setIsOpen(true);
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlightedIndex((currentIndex) => {
                if (filteredOptions.length === 0) {
                    return -1;
                }

                return currentIndex >= filteredOptions.length - 1 ? 0 : currentIndex + 1;
            });
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlightedIndex((currentIndex) => {
                if (filteredOptions.length === 0) {
                    return -1;
                }

                return currentIndex <= 0 ? filteredOptions.length - 1 : currentIndex - 1;
            });
            return;
        }

        if (event.key === "Enter" && isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            event.preventDefault();
            handleSelect(filteredOptions[highlightedIndex]);
            return;
        }

        if (event.key === "Escape") {
            setIsOpen(false);
        }
    }

    return (
        <div ref={containerRef} style={{position: "relative"}}>
            <InputGroup>
                <Form.Control
                    aria-autocomplete="list"
                    aria-expanded={isOpen}
                    aria-label="Property"
                    autoComplete="off"
                    placeholder="Search property"
                    required={required}
                    role="combobox"
                    type="text"
                    value={searchText}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                />
                <Button
                    aria-label="Toggle property results"
                    type="button"
                    variant="outline-secondary"
                    onClick={() => setIsOpen((currentValue) => !currentValue)}
                >
                    ▼
                </Button>
            </InputGroup>
            {isOpen ? (
                <div
                    className="dropdown-menu show w-100"
                    style={{
                        maxHeight: "280px",
                        overflow: "auto",
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1055,
                    }}
                >
                    {filteredOptions.length > 0 ? filteredOptions.map((property, index) => {
                        const isSelected = selectedProperty && getPropertyId(selectedProperty) === getPropertyId(property);
                        const isHighlighted = highlightedIndex === index;

                        return (
                            <button
                                key={getPropertyId(property)}
                                aria-selected={isSelected}
                                className="dropdown-item"
                                role="option"
                                style={{
                                    backgroundColor: isHighlighted || isSelected ? "#0d6efd" : "",
                                    color: isHighlighted || isSelected ? "#fff" : "",
                                }}
                                type="button"
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onClick={() => handleSelect(property)}
                            >
                                {getPropertyLabel(property)}
                            </button>
                        );
                    }) : (
                        <div className="dropdown-item disabled">No matches found</div>
                    )}
                </div>
            ) : null}
        </div>
    );
}
